import React, { useState, useEffect } from "react";
import { getPortfolio } from "../services/portfolioService";

export default function Portfolio() {
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);

  const API_BASE = process.env.REACT_APP_IMAGE_URL;

  useEffect(() => {
    document.title = "Portfolio | Development Solutions";

    // ✅ Fetch projects via service
    const fetchProjects = async () => {
      try {
        const data = await getPortfolio();
        setProjects(data);
      } catch (err) {
        console.error("Failed to load portfolio", err);
      }
    };

    fetchProjects();
  }, []);

  return (
    <>
      {/* ✅ HERO SECTION */}
      <section
        className="relative h-[50vh] flex items-center justify-center text-center"
        style={{
          backgroundImage:
            "url('https://images.pexels.com/photos/3184292/pexels-photo-3184292.jpeg?auto=compress&cs=tinysrgb&w=1600')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-black bg-opacity-70"></div>
        <div className="relative z-10 px-4 sm:px-6">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight">
            Our Portfolio
          </h1>
          <p className="mt-4 text-sm sm:text-base lg:text-lg text-gray-200 max-w-3xl mx-auto">
            We deliver impactful digital solutions – from enterprise platforms
            to sleek mobile apps. Here's a glimpse of how we've helped
            businesses grow.
          </p>
        </div>
      </section>

      {/* ✅ INTRO SECTION */}
      <section className="bg-white text-center">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 h-[40vh] flex flex-col justify-center">
          <h2
            className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4 sm:mb-6"
            style={{ color: "#414042" }}
          >
            Showcasing <span style={{ color: "#1b75bc" }}>Our Impact</span>
          </h2>
          <p className="text-sm sm:text-base lg:text-lg text-gray-600 leading-relaxed">
            Every project is a story of innovation, collaboration, and
            measurable results. We work with startups, SMEs, and enterprises to
            craft digital solutions that matter.
          </p>
        </div>
      </section>

      {/* ✅ PROJECT GRID */}
      <section className="bg-gray-50">
        <div className="max-w-7xl flex flex-col justify-center h-[140vh] mx-auto px-4 sm:px-6 text-center">
          <h2
            className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-8 sm:mb-12"
            style={{ color: "#414042" }}
          >
            Recent <span style={{ color: "#1b75bc" }}>Work</span>
          </h2>

          {/* ✅ If no projects, show loader */}
          {projects.length === 0 ? (
            <p className="text-gray-500">No Projects Found</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
              {projects.map((project) => (
                <div
                  key={project.id}
                  className="relative group rounded-lg overflow-hidden shadow hover:shadow-xl transition cursor-pointer"
                  onClick={() => setSelectedProject(project)}
                >
                  {/* ✅ Image from backend */}
                  {project.image ? (
                    <img
                      src={`${API_BASE}/${project.image}`}
                      alt={project.title}
                      className="h-48 sm:h-56 lg:h-64 w-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    <div className="h-48 sm:h-56 lg:h-64 w-full bg-gray-200 flex items-center justify-center text-gray-500">
                      No Image
                    </div>
                  )}

                  {/* Overlay */}
                  <div className="absolute inset-0 bg-black bg-opacity-50 flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <h3 className="text-lg sm:text-xl font-bold text-white px-2">
                      {project.title}
                    </h3>
                    <p className="text-xs sm:text-sm text-gray-200 mt-2 px-2">
                      {project.category}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ✅ MODAL WITH BACKEND DATA */}
      {selectedProject && (
        <div
          className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 z-50 p-4"
          onClick={() => setSelectedProject(null)}
        >
          <div
            className="bg-white rounded-lg shadow-lg max-w-3xl w-full relative p-4 sm:p-6 transform transition-all duration-300 scale-95 opacity-0 animate-fadeIn max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* ✅ Close button */}
            <button
              className="absolute top-2 right-2 sm:top-4 sm:right-4 text-gray-500 hover:text-gray-700 text-2xl"
              onClick={() => setSelectedProject(null)}
            >
              ×
            </button>

            {/* ✅ Project Image */}
            {selectedProject.image && (
              <img
                src={`${API_BASE}/${selectedProject.image}`}
                alt={selectedProject.title}
                className="w-full h-48 sm:h-64 object-cover rounded-md"
              />
            )}

            {/* ✅ Project Info */}
            <div className="mt-4 sm:mt-5">
              <h2
                className="text-xl sm:text-2xl font-bold pr-8"
                style={{ color: "#1b75bc" }}
              >
                {selectedProject.title}
              </h2>
              <p className="text-xs sm:text-sm text-gray-500 mt-1">
                {selectedProject.category}
              </p>

              <div className="mt-3 sm:mt-4 space-y-2 text-gray-700 leading-relaxed">
                {selectedProject.description && (
                  <p className="text-sm sm:text-base">
                    <strong>Description:</strong> {selectedProject.description}
                  </p>
                )}
                {selectedProject.tech_stack && (
                  <div className="mt-3 sm:mt-4">
                    <strong className="block text-gray-700 mb-2 text-sm sm:text-base">
                      Tech Stack:
                    </strong>
                    <div className="flex flex-wrap gap-2">
                      {selectedProject.tech_stack
                        .split(",")
                        .map((tech, index) => (
                          <span
                            key={index}
                            className="px-2 sm:px-3 py-1 text-xs sm:text-sm bg-blue-100 text-blue-800 rounded-full shadow-sm"
                          >
                            {tech.trim()}
                          </span>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ✅ IMPACT NUMBERS */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto text-center px-4 sm:px-6">
          <h2
            className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-8 sm:mb-12"
            style={{ color: "#414042" }}
          >
            Our <span style={{ color: "#1b75bc" }}>Achievements</span>
          </h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {[
              { num: "50+", label: "Successful Projects" },
              { num: "10+", label: "Global Clients" },
              { num: "98%", label: "Client Satisfaction" },
              { num: "7+", label: "Years of Experience" },
            ].map((stat, idx) => (
              <div key={idx}>
                <h3
                  className="text-2xl sm:text-3xl lg:text-4xl font-bold"
                  style={{ color: "#1b75bc" }}
                >
                  {stat.num}
                </h3>
                <p className="mt-2 text-xs sm:text-sm lg:text-base text-gray-600">
                  {stat.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ✅ CTA SECTION */}
      <section
        className="relative py-20 text-center"
        style={{
          backgroundImage:
            "url('https://images.pexels.com/photos/3184418/pexels-photo-3184418.jpeg?auto=compress&cs=tinysrgb&w=1600')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-black bg-opacity-70"></div>
        <div className="relative z-10 px-4 sm:px-6">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white">
            Let's Build Your Success Story
          </h2>
          <p className="mt-4 text-sm sm:text-base lg:text-lg text-gray-200 max-w-xl mx-auto">
            Ready to start your own project? Let's collaborate to create
            something extraordinary.
          </p>
          <a
            href="/contact"
            className="mt-6 inline-block px-6 sm:px-8 py-3 sm:py-4 rounded-md text-white font-bold"
            style={{ backgroundColor: "#1b75bc" }}
          >
            Start Your Project
          </a>
        </div>
      </section>

      {/* ✅ Fade-in animation */}
      <style>{`
        @keyframes fadeIn {
          0% { opacity: 0; transform: scale(0.95); }
          100% { opacity: 1; transform: scale(1); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out forwards;
        }
      `}</style>
    </>
  );
}
